import React from 'react';
import { motion } from 'framer-motion';
import { Star, MapPin, Wifi, Waves, Utensils, Dumbbell, Calendar, Users } from 'lucide-react';
import { useBooking, Hotel } from '../hooks/useBooking';
const MOCK_HOTELS: Hotel[] = [{
  id: 'h1',
  name: 'Bali Serenity Resort',
  location: 'Bali',
  price: 175,
  rating: 4.8,
  description: 'Tropical paradise nestled in lush jungle with a private beach, traditional Balinese spa, and infinity pool overlooking rice terraces.',
  image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?auto=format&fit=crop&w=800&q=80',
  amenities: ['Spa', 'Pool', 'Restaurant', 'WiFi']
}, {
  id: 'h2',
  name: 'Ubud Hanging Gardens',
  location: 'Bali',
  price: 320,
  rating: 4.9,
  description: 'Award-winning resort perched above the Ayung River gorge with dual-level infinity pools and world-class Balinese cuisine.',
  image: 'https://images.unsplash.com/photo-1540541338287-41700207dee6?auto=format&fit=crop&w=800&q=80',
  amenities: ['Pool', 'Spa', 'Restaurant', 'WiFi']
}, {
  id: 'h3',
  name: 'Seminyak Beach Villas',
  location: 'Bali',
  price: 245,
  rating: 4.7,
  description: "Chic beachfront villas on Seminyak's golden sands with private pools, sunset bars, and easy access to vibrant nightlife.",
  image: 'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?auto=format&fit=crop&w=800&q=80',
  amenities: ['Pool', 'WiFi', 'Restaurant', 'Gym']
}, {
  id: 'h4',
  name: 'Nusa Dua Royal Retreat',
  location: 'Bali',
  price: 210,
  rating: 4.6,
  description: 'Elegant oceanfront resort in the exclusive Nusa Dua enclave with pristine white sand beach and championship golf course.',
  image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?auto=format&fit=crop&w=800&q=80',
  amenities: ['Pool', 'Gym', 'Restaurant', 'Spa']
}, {
  id: 'h5',
  name: 'Canggu Surf Lodge',
  location: 'Bali',
  price: 145,
  rating: 4.5,
  description: "Laid-back luxury lodge steps from Bali's best surf breaks, with rooftop yoga deck and organic farm-to-table dining.",
  image: 'https://images.unsplash.com/photo-1559628233-100c798642d4?auto=format&fit=crop&w=800&q=80',
  amenities: ['WiFi', 'Restaurant', 'Pool']
}, {
  id: 'h6',
  name: 'Jimbaran Cliff Resort',
  location: 'Bali',
  price: 280,
  rating: 4.8,
  description: 'Dramatic clifftop resort above Jimbaran Bay with private rock-bar, ocean-view suites, and legendary seafood dining on the beach.',
  image: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?auto=format&fit=crop&w=800&q=80',
  amenities: ['Pool', 'Restaurant', 'Spa', 'WiFi']
}];
const getAmenityIcon = (amenity: string) => {
  switch (amenity) {
    case 'WiFi':
      return <Wifi className="w-4 h-4" data-id="element-420" />;
    case 'Pool':
      return <Waves className="w-4 h-4" data-id="element-421" />;
    case 'Restaurant':
      return <Utensils className="w-4 h-4" data-id="element-422" />;
    case 'Gym':
      return <Dumbbell className="w-4 h-4" data-id="element-423" />;
    default:
      return null;
  }
};
interface SearchResultsProps {
  onSelectHotel: () => void;
  onEditSearch: () => void;
}
export function SearchResults({
  onSelectHotel,
  onEditSearch
}: SearchResultsProps) {
  const {
    location,
    checkIn,
    checkOut,
    adults,
    rooms,
    selectHotel
  } = useBooking();
  // Filter hotels by location if one is set, otherwise show all
  const filteredHotels = location ? MOCK_HOTELS.filter(h => h.location.toLowerCase() === location.toLowerCase()) : MOCK_HOTELS;
  // If no exact match, show all as "Recommended"
  const displayHotels = filteredHotels.length > 0 ? filteredHotels : MOCK_HOTELS;
  const isExactMatch = filteredHotels.length > 0;
  const formatDate = (date: Date | null) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  const handleSelect = (hotel: Hotel) => {
    selectHotel(hotel);
    onSelectHotel();
  };
  return <div className="pt-24 pb-20 min-h-screen bg-luxury-cream" data-id="element-424">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-425">
        {/* Search Summary Bar */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col md:flex-row items-center justify-between mb-8" data-id="element-426">
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 font-medium mb-4 md:mb-0" data-id="element-427">
            <div className="flex items-center bg-gray-50 px-3 py-1.5 rounded-lg" data-id="element-428">
              <MapPin className="w-4 h-4 mr-2 text-luxury-blue" data-id="element-429" />
              {location || 'Anywhere'}
            </div>
            <div className="flex items-center bg-gray-50 px-3 py-1.5 rounded-lg" data-id="element-430">
              <Calendar className="w-4 h-4 mr-2 text-luxury-blue" data-id="element-431" />
              {checkIn && checkOut ? `${formatDate(checkIn)} - ${formatDate(checkOut)}` : 'Dates not set'}
            </div>
            <div className="flex items-center bg-gray-50 px-3 py-1.5 rounded-lg" data-id="element-432">
              <Users className="w-4 h-4 mr-2 text-luxury-blue" data-id="element-433" />
              {adults} Adults, {rooms} Room{rooms > 1 ? 's' : ''}
            </div>
          </div>
          <button onClick={onEditSearch} className="text-luxury-blue font-semibold text-sm hover:text-blue-700 transition-colors" data-id="element-434">
            Edit Search
          </button>
        </motion.div>

        {/* Heading */}
        <div className="mb-8" data-id="element-435">
          <h1 className="text-3xl md:text-4xl font-italiana text-luxury-slate" data-id="element-436">
            {isExactMatch && location ? `${displayHotels.length} properties found in ${location}` : `Recommended properties for you`}
          </h1>
        </div>

        {/* Hotel List */}
        <div className="space-y-6" data-id="element-437">
          {displayHotels.map((hotel, index) => <motion.div key={hotel.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-float transition-shadow duration-300 border border-gray-100 flex flex-col md:flex-row" data-id="element-438">
              {/* Image */}
              <div className="w-full md:w-72 h-56 md:h-auto shrink-0 relative" data-id="element-439">
                <img src={hotel.image} alt={hotel.name} className="w-full h-full object-cover" data-id="element-440" />
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col flex-1" data-id="element-441">
                <div className="flex justify-between items-start mb-2" data-id="element-442">
                  <div data-id="element-443">
                    <div className="flex items-center space-x-1 mb-1" data-id="element-444">
                      {[...Array(5)].map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < Math.floor(hotel.rating) ? 'fill-luxury-gold text-luxury-gold' : 'text-gray-300'}`} data-id="element-445" />)}
                    </div>
                    <h2 className="text-2xl font-italiana font-bold text-luxury-slate" data-id="element-446">
                      {hotel.name}
                    </h2>
                    <p className="text-sm text-gray-500 flex items-center mt-1" data-id="element-447">
                      <MapPin className="w-3.5 h-3.5 mr-1" data-id="element-448" /> {hotel.location}
                    </p>
                  </div>
                  <div className="text-right" data-id="element-449">
                    <div className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1" data-id="element-450">
                      From
                    </div>
                    <div className="text-2xl font-bold text-luxury-slate" data-id="element-451">
                      £{hotel.price}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-452">per night</div>
                  </div>
                </div>

                <p className="text-gray-600 text-sm mt-3 mb-6 line-clamp-2" data-id="element-453">
                  {hotel.description}
                </p>

                <div className="mt-auto flex items-center justify-between" data-id="element-454">
                  <div className="flex space-x-3" data-id="element-455">
                    {hotel.amenities.map(amenity => <div key={amenity} className="flex items-center text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded-md" data-id="element-456">
                        {getAmenityIcon(amenity)}
                        <span className="ml-1.5" data-id="element-457">{amenity}</span>
                      </div>)}
                  </div>
                  <button onClick={() => handleSelect(hotel)} className="bg-luxury-blue hover:bg-blue-600 text-white px-6 py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm" data-id="element-458">
                    Select Room
                  </button>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </div>;
}